/**
 * What is new about a client, out of the record and what this browser has
 * already seen.
 *
 * Kept apart from `clientMapping`, which turns a record into the strings a
 * screen draws. This is not about the client: it is about the reader, and the
 * same record is news to one browser and old news to another. See
 * `openedClients`.
 *
 * Two pieces of news, and they are genuinely different things:
 *
 *   - a client who has landed and nobody at this desk has opened. The row says
 *     "New", the same word an unopened enquiry wears;
 *   - a pre-consultation form that has come back and not been read. The row
 *     carries a dot in front of the name, because this one is work waiting:
 *     somebody has answered forty questions and they are sitting there.
 *
 * Both can be true of one client — one added this morning who has already sent
 * their form back — and the row draws both, in two places that do not crowd
 * each other.
 */

import type {
  PreconsultationFormSummary,
  WaitingSubmission,
} from "../preconsultation/services/preconsultationFormsService";
import type { ClientRecord } from "./types/clients.types";

/**
 * The form waiting on the practitioner: answers sent back, and nobody has
 * ruled on them yet.
 *
 * `SUBMITTED` is the backend's own word for exactly that state — a form that
 * has been accepted or returned has moved to `APPROVED` or `REJECTED`, and one
 * still out is `PENDING`. So this is a read of the wire and not a judgement:
 * the same state `governingForm` puts at the top of its precedence, for the
 * same reason.
 *
 * The first where a client somehow has two, since the backend lists them
 * newest first and the strip above their tabs speaks for one form at a time.
 */
export const submittedFormOf = (
  record: Pick<ClientRecord, "preconsultationForms">
): PreconsultationFormSummary | null =>
  record.preconsultationForms.find((form) => form.status === "SUBMITTED") ??
  null;

/**
 * Every key one submission could be remembered by, most particular first.
 *
 * Not simply the form's `public_id`, which is what this was and why the flag
 * never lit: a form is read off two endpoints that do not carry the same
 * fields. The client's own read has the whole record; the list's copy of it can
 * be trimmed, and a trimmed form that says `SUBMITTED` without saying which
 * form it is left `id` as `""` — falsy, so the row asked "is there a form
 * waiting?" and answered no while the answers sat on the client's screen.
 *
 * So the submission is named by whatever the response actually carried, and
 * the client's id is on the front of all of them: keys from two workspaces
 * cannot collide, and a key is meaningless without the client anyway. The last
 * one needs no fields at all, so there is always at least one.
 *
 * Which end uses which is the point:
 *
 *   - a **list** row takes the first key it can build. Trimmed or whole, it
 *     names the submission as precisely as its own data allows;
 *   - the **client's screen** marks every one of them, because its record is a
 *     superset of the list's — so whichever key the list ends up asking about,
 *     it has been marked, and the flag clears.
 *
 * The moments are what make a second form news again: a client sent another
 * link next month submits at a different `submitted_at`, so the key moves and
 * the flag comes back. Only the bare fallback cannot tell two submissions
 * apart, and that is reached only by a response carrying no moment and no id —
 * which could not have told them apart either.
 */
export const submissionKeys = (
  clientId: string,
  form: PreconsultationFormSummary
): string[] => {
  const named = [form.id, form.submittedAt, form.createdAt]
    .map((part) => (part ?? "").trim())
    .filter(Boolean)
    .map((part) => `${clientId}:${part}`);

  return [...named, `${clientId}:submitted`];
};

/**
 * The little of a client any of this is read off.
 *
 * Three fields rather than the record, so a list row can answer for itself:
 * `Client` carries exactly these, which is what lets the table ask about a row
 * it is already holding instead of being handed a second structure keyed by id.
 */
export interface NewsSubject {
  /** The client's `public_id`. */
  id: string;
  /** ISO moment they landed on the books. */
  createdAt: string;
  /**
   * How the submission waiting on the practitioner is remembered, or `null`
   * where there is none waiting. The most particular key the record allowed —
   * see `submissionKeys`.
   */
  submissionKey: string | null;
  /**
   * ISO moment those answers came back, or `null` where the response did not
   * say. Compared against the moment this browser started counting, the same
   * way `createdAt` is — see `newsFor`.
   */
  submittedAt: string | null;
}

/** The three fields off a whole record. */
export const newsSubjectOf = (
  record: Pick<ClientRecord, "publicId" | "createdAt" | "preconsultationForms">
): NewsSubject => {
  const submitted = submittedFormOf(record);

  return {
    id: record.publicId,
    createdAt: record.createdAt,
    submissionKey: submitted
      ? submissionKeys(record.publicId, submitted)[0]
      : null,
    submittedAt: submitted?.submittedAt ?? null,
  };
};

/** Every key the submission waiting on a client could be remembered by. */
export const submissionKeysOf = (
  record: Pick<ClientRecord, "publicId" | "preconsultationForms">
): string[] => {
  const submitted = submittedFormOf(record);
  return submitted ? submissionKeys(record.publicId, submitted) : [];
};

/**
 * Submissions waiting to be read, by the client they belong to — the forms
 * endpoint's answer, in the shape a row asks its question in.
 *
 * A second source for the same fact, and the reason there are two: a client's
 * own record carries the forms raised for them, but the *list* of clients does
 * not, so a row read from the list cannot tell whether answers are sitting
 * there. The forms endpoint can, for every client at once. Where both speak
 * the record wins — it is the same form, named the same way, so they agree.
 */
export type WaitingSubmissions = ReadonlyMap<string, WaitingForm>;

/** One submission waiting to be read: how it is remembered, and when it came back. */
export interface WaitingForm {
  /** The key the row asks the seen set about — see `submissionKeys`. */
  key: string;
  /** ISO moment the answers came back, or `null` where the response did not say. */
  submittedAt: string | null;
}

/** The map the screens hold, keyed by client. */
export const waitingByClient = (
  submissions: readonly WaitingSubmission[]
): WaitingSubmissions =>
  new Map(
    submissions.map((submission) => [
      submission.clientId,
      {
        key: submissionKeys(submission.clientId, {
          id: submission.formId,
          submittedAt: submission.submittedAt,
          createdAt: submission.createdAt,
        } as PreconsultationFormSummary)[0],
        submittedAt: submission.submittedAt,
      },
    ])
  );

/** Nothing waiting — what the screens use before the read lands, or if it never does. */
export const NO_WAITING: WaitingSubmissions = new Map();

/** What is new about one client, for the flags a row wears. */
export interface ClientNews {
  /** They have landed since this browser started counting, unopened. */
  unopened: boolean;
  /** Their answers have come back and nobody here has read them. */
  formWaiting: boolean;
}

/**
 * How many things there are to look at about one client — nought, one, or
 * both.
 *
 * Both counted rather than the client counted once, because the badge has to
 * move when something happens. A client added this morning is already on the
 * count; if their answers coming back this afternoon did not add to it, the
 * one arrival this feature exists to announce would pass in silence — the
 * number would sit at what it already said and the practitioner would have no
 * reason to look. Two marks on the row, two on the count.
 */
export const newsCount = (news: ClientNews): number =>
  (news.unopened ? 1 : 0) + (news.formWaiting ? 1 : 0);

/**
 * When a client landed, as a number. `null` for a date that will not parse,
 * which reads below as "not news" — a client the app cannot date is not one to
 * make a claim about.
 */
const arrivedAt = (subject: NewsSubject): number | null => {
  const ms = Date.parse(subject.createdAt ?? "");
  return Number.isNaN(ms) ? null : ms;
};

/**
 * Whether answers came back since this browser started counting.
 *
 * A submission the response did not date is let through: the wire says the
 * answers are sitting there, and the line exists to quieten what it can prove
 * is older, not to doubt what it cannot date. The backend stamps every
 * submission, so this is a shape the app is unlikely to meet.
 */
const cameBackSince = (form: WaitingForm, since: number): boolean => {
  const ms = Date.parse(form.submittedAt ?? "");
  return Number.isNaN(ms) || ms >= since;
};

/**
 * What is new about one client, given the ids this browser has seen and the
 * moment it started counting.
 *
 * `since` is what keeps the tag meaning "arrived" rather than "never opened".
 * Every client already on the books when this browser met the feature is old
 * news whether or not anybody ever opened them — see `clientsNewsSince`.
 *
 * The form is held to the same line, and for the same reason. The seen set is
 * only this browser's memory: clear its storage, or sign in on another
 * device, and every submission still at `SUBMITTED` would light up again,
 * however long ago it was read. So answers that came back before this browser
 * started counting are not announced — the practitioner rules on them from
 * the client's screen, where they are shown regardless. Only what arrives
 * after the line is news, and it is news once.
 */
export const newsFor = (
  subject: NewsSubject,
  seen: ReadonlySet<string>,
  since: number,
  waiting: WaitingSubmissions = NO_WAITING
): ClientNews => {
  const arrived = arrivedAt(subject);
  // The record's own word first, and the forms endpoint where the record was
  // read off a list that does not carry them. See `WaitingSubmissions`.
  const form: WaitingForm | null =
    subject.submissionKey !== null
      ? { key: subject.submissionKey, submittedAt: subject.submittedAt }
      : waiting.get(subject.id) ?? null;

  return {
    unopened: !seen.has(subject.id) && arrived !== null && arrived >= since,
    formWaiting:
      form !== null && !seen.has(form.key) && cameBackSince(form, since),
  };
};

/**
 * How much there is to look at across a list of clients — what the rail's
 * badge shows.
 *
 * A sum of the pieces, not a headcount of the clients carrying them. See
 * `newsCount`.
 */
export const countClientNews = (
  records: Pick<
    ClientRecord,
    "publicId" | "createdAt" | "preconsultationForms"
  >[],
  seen: ReadonlySet<string>,
  since: number,
  waiting: WaitingSubmissions = NO_WAITING
): number =>
  records.reduce(
    (total, record) =>
      total + newsCount(newsFor(newsSubjectOf(record), seen, since, waiting)),
    0
  );
